import type {
  AppPermission,
  AvailabilityPrefs,
  CoverRequest,
  CoverRequestStatus,
  DayOfWeek,
  ScheduleEvent,
  User,
} from '../types'

/* ------------------------------------------------------------------ *
 * Needs Cover
 *
 * The rules live here rather than in components so the same answers are
 * given on every screen and in every interface mode, and so the workflow
 * can move behind Supabase later by swapping the state layer only.
 * ------------------------------------------------------------------ */

/** Only shifts are covered — appointments and meetings are not rostered work. */
export function isCoverableShift(event: ScheduleEvent): boolean {
  return event.type === 'shift'
}

export function needsCover(event: ScheduleEvent): boolean {
  return isCoverableShift(event) && event.cover?.status === 'needs_cover'
}

/** True once a replacement has taken a shift that was previously put out. */
export function wasCovered(event: ScheduleEvent): boolean {
  return isCoverableShift(event) && event.cover?.status === 'covered'
}

export function eventsNeedingCover(events: ScheduleEvent[]): ScheduleEvent[] {
  return events
    .filter(needsCover)
    .sort((a, b) => (a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date)))
}

export function datesNeedingCover(events: ScheduleEvent[]): Set<string> {
  return new Set(events.filter(needsCover).map((e) => e.date))
}

/* ------------------------------------------------------------------ *
 * Availability
 * ------------------------------------------------------------------ */

export const DAY_KEYS: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export const DAY_LABELS: Record<DayOfWeek, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
}

/** Monday-first ordering for the pickers, which reads as a working week. */
export const WEEK_DAYS: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export function dayOfWeekFor(dateISO: string): DayOfWeek {
  return DAY_KEYS[new Date(dateISO + 'T00:00:00').getDay()]
}

/**
 * "I may be available" — never "I have accepted this shift". Availability only
 * counts when the worker chose to share it with the participant's calendar.
 */
export function isAvailableOnDate(prefs: AvailabilityPrefs | undefined, dateISO: string): boolean {
  if (!prefs || !prefs.showOnCalendar) return false
  return prefs.availableDays.includes(dayOfWeekFor(dateISO))
}

/* ------------------------------------------------------------------ *
 * Candidates
 * ------------------------------------------------------------------ */

export interface CoverCandidate {
  user: User
  /** Marked available for that weekday and sharing it with the calendar. */
  available: boolean
  /** An overlapping commitment already in the calendar, if any. */
  conflict?: ScheduleEvent
  /** The most recent cover request this person has for the shift. */
  request?: CoverRequest
  /** True when this is the worker who withdrew from this very shift. */
  withdrewFromShift: boolean
}

/** Two calendar entries that clash in time on the same day. */
export function overlaps(a: ScheduleEvent, b: ScheduleEvent): boolean {
  if (a.date !== b.date) return false
  return a.startTime < b.endTime && b.startTime < a.endTime
}

export function conflictFor(userId: string, event: ScheduleEvent, events: ScheduleEvent[]): ScheduleEvent | undefined {
  return events.find(
    (other) =>
      other.id !== event.id &&
      overlaps(other, event) &&
      other.assignees.some((a) => a.userId === userId && a.status !== 'declined'),
  )
}

/**
 * Who the participant may ask to cover a shift.
 *
 * Strictly the participant's own team: a person must already be in this SCHub
 * space and hold `cover_shifts`, which by default means the support workers.
 * There is no wider search and no recommendation of unknown workers.
 */
export function buildCoverCandidates({
  event,
  teamMembers,
  events,
  coverRequests,
  availabilityFor,
  permissionsFor,
}: {
  event: ScheduleEvent
  teamMembers: User[]
  events: ScheduleEvent[]
  coverRequests: CoverRequest[]
  availabilityFor: (userId: string) => AvailabilityPrefs | undefined
  permissionsFor: (userId: string) => AppPermission[]
}): CoverCandidate[] {
  const alreadyOn = new Set(event.assignees.map((a) => a.userId))

  return teamMembers
    .filter((user) => permissionsFor(user.id).includes('cover_shifts'))
    .filter((user) => !alreadyOn.has(user.id))
    .map((user) => {
      const requests = coverRequests
        .filter((r) => r.eventId === event.id && r.requestedUserId === user.id)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      return {
        user,
        available: isAvailableOnDate(availabilityFor(user.id), event.date),
        conflict: conflictFor(user.id, event, events),
        request: requests[requests.length - 1],
        withdrewFromShift: event.cover?.originalAssigneeUserId === user.id,
      }
    })
    .sort((a, b) => {
      // Available and free first, then available but clashing, then the rest.
      const score = (c: CoverCandidate) => (c.available ? 0 : 2) + (c.conflict ? 1 : 0)
      const diff = score(a) - score(b)
      return diff !== 0 ? diff : a.user.name.localeCompare(b.user.name)
    })
}

/* ------------------------------------------------------------------ *
 * Presentation helpers
 * ------------------------------------------------------------------ */

export const COVER_REQUEST_STATUS_META: Record<CoverRequestStatus, { label: string; style: string }> = {
  pending: { label: 'Waiting for a reply', style: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', style: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Can’t do it', style: 'bg-slate-100 text-slate-500' },
  closed: { label: 'Filled — no longer needed', style: 'bg-slate-100 text-slate-500' },
}

export function shiftDateLabel(dateISO: string): string {
  return new Date(dateISO + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/** One line describing a shift, used in cover request messages and cards. */
export function shiftSummary(event: ScheduleEvent): string {
  return `${event.title} — ${shiftDateLabel(event.date)}, ${event.startTime}–${event.endTime}`
}

